import type { FormEvent } from 'react';
import PageLayout from '../../components/feature/PageLayout';
import PageHero from '../../components/feature/PageHero';
import PageSectionNav from '../../components/feature/PageSectionNav';
import { useTranslation } from 'react-i18next';
import { submitLocalForm } from '../../lib/localForms';

const services = [
  {
    id: 'bible-study',
    title: 'Bible Study',
    titleKey: 'services.bibleStudy.title',
    icon: 'ri-book-read-line',
    schedule: 'Every Saturday, 5:00 PM',
    scheduleKey: 'services.bibleStudy.schedule',
    location: 'Church Classrooms',
    locationKey: 'services.bibleStudy.location',
    description:
      'Deepen your understanding of Holy Scripture through our weekly Bible Study sessions. Led by our clergy and experienced teachers, these gatherings explore the Old and New Testaments, the writings of the Church Fathers, and how to apply biblical wisdom to daily life.',
    descKey: 'services.bibleStudy.desc',
    features: ['Verse-by-verse study', 'Group discussions', 'Q&A sessions', 'Study materials provided'],
    featureKeys: ['services.bibleStudy.feature1', 'services.bibleStudy.feature2', 'services.bibleStudy.feature3', 'services.bibleStudy.feature4'],
    image:
      '/assets/readdy/f7b4a485459bdc8fb8c9e8bfa7152063-a681f3bea683.jpeg',
  },
  {
    id: 'hymns-class',
    title: 'Hymns Class',
    titleKey: 'services.hymnsClass.title',
    icon: 'ri-music-2-line',
    schedule: 'Every Saturday, 7:00 PM',
    scheduleKey: 'services.hymnsClass.schedule',
    location: 'Church Classrooms',
    locationKey: 'services.hymnsClass.location',
    description:
      'Learn the beautiful and ancient Coptic hymns that have been passed down through generations. Our Hymns Class teaches the traditional melodies, pronunciation, and spiritual meaning behind each hymn, preserving our rich liturgical heritage.',
    descKey: 'services.hymnsClass.desc',
    features: ['Coptic language instruction', 'Traditional melodies', 'Hymn history & meaning', 'All skill levels welcome'],
    featureKeys: ['services.hymnsClass.feature1', 'services.hymnsClass.feature2', 'services.hymnsClass.feature3', 'services.hymnsClass.feature4'],
    image:
      '/assets/readdy/47b57d505a007456029b9a65e4b7fe3d-0e6f8da2f058.jpeg',
  },
  {
    id: 'sunday-school',
    title: 'Sunday School',
    titleKey: 'services.sundaySchool.title',
    icon: 'ri-graduation-cap-line',
    schedule: 'Every Sunday, 12:30 PM',
    scheduleKey: 'services.sundaySchool.schedule',
    location: 'Church Classrooms',
    locationKey: 'services.sundaySchool.location',
    description:
      "Our Sunday School program provides faith education for children and youth of all ages. Through engaging lessons, activities, and discussions, young members learn about the Bible, Coptic traditions, saints' lives, and how to live as faithful Christians.",
    descKey: 'services.sundaySchool.desc',
    features: ['Age-appropriate classes', 'Interactive lessons', 'Arts & crafts activities', 'Annual competitions'],
    featureKeys: ['services.sundaySchool.feature1', 'services.sundaySchool.feature2', 'services.sundaySchool.feature3', 'services.sundaySchool.feature4'],
    image:
      '/assets/readdy/a384e47f79b9a5ed8bd2a6ec6741d7e6-d1aff3695845.jpeg',
  },
  {
    id: 'youth-meeting',
    title: 'Youth Meeting',
    titleKey: 'services.youthMeeting.title',
    icon: 'ri-team-line',
    schedule: 'Every Friday, 8:30 PM',
    scheduleKey: 'services.youthMeeting.schedule',
    location: 'Church Basement',
    locationKey: 'services.youthMeeting.location',
    description:
      "A vibrant community for young adults to grow in faith together. Our Youth Meeting combines spiritual discussions, fellowship activities, and service opportunities. It's a safe space to ask questions, build friendships, and strengthen your relationship with God.",
    descKey: 'services.youthMeeting.desc',
    features: ['Spiritual discussions', 'Social activities', 'Service projects', 'Retreats & trips'],
    featureKeys: ['services.youthMeeting.feature1', 'services.youthMeeting.feature2', 'services.youthMeeting.feature3', 'services.youthMeeting.feature4'],
    image:
      '/assets/readdy/a421a148911c7c1b8e2c826085dda6f5-e367cf211529.jpeg',
  },
  {
    id: 'family-meeting',
    title: 'Family Meeting',
    titleKey: 'services.familyMeeting.title',
    icon: 'ri-home-heart-line',
    schedule: 'Every Friday, 8:30 PM',
    scheduleKey: 'services.familyMeeting.schedule',
    location: "Pope Cyril's Hallway",
    locationKey: 'services.familyMeeting.location',
    description:
      'Strengthen family bonds through faith with our monthly Family Meeting. Parents and children come together for spiritual guidance, parenting discussions, and family activities that help build Christ-centered homes.',
    descKey: 'services.familyMeeting.desc',
    features: ['Family worship', 'Parenting guidance', "Children's activities", 'Fellowship dinner'],
    featureKeys: ['services.familyMeeting.feature1', 'services.familyMeeting.feature2', 'services.familyMeeting.feature3', 'services.familyMeeting.feature4'],
    image:
      '/assets/readdy/9479356d5b1e5e45cd6ccdd9d69d5cc3-34e135ed265d.jpeg',
  },
  {
    id: 'seniors-meeting',
    title: 'Seniors Meeting',
    titleKey: 'services.seniorsMeeting.title',
    icon: 'ri-heart-2-line',
    schedule: 'Every Thursday, 11:00 AM',
    scheduleKey: 'services.seniorsMeeting.schedule',
    location: 'Parish Hall',
    locationKey: 'services.seniorsMeeting.location',
    description:
      'A cherished gathering for our senior members to enjoy fellowship, spiritual enrichment, and community support. The Seniors Meeting includes prayer, health discussions, social activities, and opportunities to share wisdom with younger generations.',
    descKey: 'services.seniorsMeeting.desc',
    features: ['Prayer & devotion', 'Health seminars', 'Social activities', 'Lunch provided'],
    featureKeys: ['services.seniorsMeeting.feature1', 'services.seniorsMeeting.feature2', 'services.seniorsMeeting.feature3', 'services.seniorsMeeting.feature4'],
    image:
      '/assets/readdy/2040e6d62fe7849e8b98ae3968270f24-7ea7bcfdde70.jpeg',
  },
  {
    id: 'church-choirs',
    title: 'Church Choirs',
    titleKey: 'services.churchChoirs.title',
    icon: 'ri-mic-line',
    schedule: 'Practice: Saturdays, 4:00 PM',
    scheduleKey: 'services.churchChoirs.schedule',
    location: 'Church Sanctuary',
    locationKey: 'services.churchChoirs.location',
    description:
      'Our Church Choirs lead the congregation in sacred worship through beautiful Coptic hymns and responses. We have choirs for different age groups, from children to adults, all dedicated to glorifying God through music.',
    descKey: 'services.churchChoirs.desc',
    features: ['Adult choir', 'Youth choir', "Children's choir", 'Special occasions'],
    featureKeys: ['services.churchChoirs.feature1', 'services.churchChoirs.feature2', 'services.churchChoirs.feature3', 'services.churchChoirs.feature4'],
    image:
      '/assets/readdy/d981c5bd9493a035df3b3614be365232-aea16e2b3dd3.jpeg',
  },
  {
    id: 'theatre-service',
    title: 'Theatre Service',
    titleKey: 'services.theatreService.title',
    icon: 'ri-movie-2-line',
    schedule: 'Seasonal Performances',
    scheduleKey: 'services.theatreService.schedule',
    location: 'Church Hall',
    locationKey: 'services.theatreService.location',
    description:
      'Our Theatre Ministry brings scripture and the lives of the saints to life through powerful dramatic performances. From Christmas and Easter plays to educational skits for Sunday School, our talented members use the arts to inspire faith and deepen spiritual understanding.',
    descKey: 'services.theatreService.desc',
    features: ['Biblical plays', "Saints' life dramas", 'Youth performances', 'Holiday productions'],
    featureKeys: ['services.theatreService.feature1', 'services.theatreService.feature2', 'services.theatreService.feature3', 'services.theatreService.feature4'],
    image:
      '/assets/readdy/649fb05c7a555b91d1ed960720818f6d-3a8bfff9f295.jpeg',
  },
  {
    id: 'it-media',
    title: 'IT & Media Services',
    titleKey: 'services.itMedia.title',
    icon: 'ri-computer-line',
    schedule: 'Ongoing Support',
    scheduleKey: 'services.itMedia.schedule',
    location: 'Media Room',
    locationKey: 'services.itMedia.location',
    description:
      'Our IT & Media team ensures our church stays connected with the community through technology. From live-streaming services to managing our website and social media, this team helps spread the Gospel using modern tools.',
    descKey: 'services.itMedia.desc',
    features: ['Live streaming', 'Website management', 'Social media', 'Audio/visual support'],
    featureKeys: ['services.itMedia.feature1', 'services.itMedia.feature2', 'services.itMedia.feature3', 'services.itMedia.feature4'],
    image:
      '/assets/readdy/service-media-008-c948c1643b18.jpg',
  },
  {
    id: 'kitchen-services',
    title: 'Kitchen Services',
    titleKey: 'services.kitchenServices.title',
    icon: 'ri-restaurant-line',
    schedule: 'Sundays & Special Events',
    scheduleKey: 'services.kitchenServices.schedule',
    location: 'Church Kitchen',
    locationKey: 'services.kitchenServices.location',
    description:
      'Our Kitchen Services team prepares meals for fellowship after liturgy and special church events. This ministry of hospitality brings the community together around the table, continuing the tradition of breaking bread together.',
    descKey: 'services.kitchenServices.desc',
    features: ['Sunday agape meals', 'Event catering', 'Fasting menu options', 'Volunteer opportunities'],
    featureKeys: ['services.kitchenServices.feature1', 'services.kitchenServices.feature2', 'services.kitchenServices.feature3', 'services.kitchenServices.feature4'],
    image:
      '/assets/readdy/service-kitchen-009-3f8b8638471b.jpg',
  },
  {
    id: 'holy-bread',
    title: 'Holy Bread Services',
    titleKey: 'services.holyBread.title',
    icon: 'ri-cake-3-line',
    schedule: 'Saturdays, Early Morning',
    scheduleKey: 'services.holyBread.schedule',
    location: 'Bread Preparation Room',
    locationKey: 'services.holyBread.location',
    description:
      'The Holy Bread (Korban) service is a sacred ministry where volunteers prepare the bread used for Holy Communion. This ancient tradition requires prayer, fasting, and devotion as the bread is baked with special prayers and hymns.',
    descKey: 'services.holyBread.desc',
    features: ['Sacred preparation', 'Traditional methods', 'Prayer & hymns', 'Training available'],
    featureKeys: ['services.holyBread.feature1', 'services.holyBread.feature2', 'services.holyBread.feature3', 'services.holyBread.feature4'],
    image:
      '/assets/readdy/711818fde86c7ccc863db84b7b04ae9a-167a6523b63c.jpeg',
  },
  {
    id: 'cleaning-services',
    title: 'Cleaning Services',
    titleKey: 'services.cleaningServices.title',
    icon: 'ri-sparkling-line',
    schedule: 'Weekly & As Needed',
    scheduleKey: 'services.cleaningServices.schedule',
    location: 'Entire Church Campus',
    locationKey: 'services.cleaningServices.location',
    description:
      "Our Cleaning Services team maintains the beauty and sanctity of God's house. These dedicated volunteers ensure our church, halls, and grounds are clean and welcoming for all who enter to worship.",
    descKey: 'services.cleaningServices.desc',
    features: ['Church sanctuary care', 'Hall maintenance', 'Grounds keeping', 'Event setup/cleanup'],
    featureKeys: ['services.cleaningServices.feature1', 'services.cleaningServices.feature2', 'services.cleaningServices.feature3', 'services.cleaningServices.feature4'],
    image:
      '/assets/readdy/service-clean-011-3861358a8685.jpg',
  },
  {
    id: 'homeless-services',
    title: 'Homeless Services',
    titleKey: 'services.homelessServices.title',
    icon: 'ri-hand-heart-line',
    schedule: 'Monthly Outreach',
    scheduleKey: 'services.homelessServices.schedule',
    location: 'Community Outreach',
    locationKey: 'services.homelessServices.location',
    description:
      "Following Christ's call to serve the least among us, our Homeless Services ministry provides food, clothing, and support to those in need. We organize regular outreach events and partner with local shelters to make a difference.",
    descKey: 'services.homelessServices.desc',
    features: ['Food distribution', 'Clothing drives', 'Shelter partnerships', 'Care packages'],
    featureKeys: ['services.homelessServices.feature1', 'services.homelessServices.feature2', 'services.homelessServices.feature3', 'services.homelessServices.feature4'],
    image:
      '/assets/readdy/service-homeless-012-d56492405544.jpg',
  },
  {
    id: 'library-service',
    title: 'Library Service',
    titleKey: 'services.libraryService.title',
    icon: 'ri-book-3-line',
    schedule: 'Open Sundays & By Request',
    scheduleKey: 'services.libraryService.schedule',
    location: 'Church Library',
    locationKey: 'services.libraryService.location',
    description:
      'Our Church Library offers a wealth of spiritual resources including books on Orthodox theology, saints\' lives, Coptic history, children\'s materials, and multimedia resources. Borrow materials to deepen your faith journey.',
    descKey: 'services.libraryService.desc',
    features: ['Theological books', "Children's section", 'Audio/video resources', 'Study materials'],
    featureKeys: ['services.libraryService.feature1', 'services.libraryService.feature2', 'services.libraryService.feature3', 'services.libraryService.feature4'],
    image:
      '/assets/readdy/7ac721db4a64cdc5cbeda052503940ae-585ea9070b32.jpeg',
  },
];

export default function ServicesPage() {
  const { t } = useTranslation();
  const handleJoinSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    await submitLocalForm('Services Join Us', form);
    form.reset();
  };

  return (
    <PageLayout hasHero>
      <PageHero
        title={t('servicesPage.title')}
        subtitle={t('servicesPage.subtitle')}
        backgroundImage="/assets/readdy/55eb7943-6622-4ed4-a6b9-5e1f70851100_IMG_0507-e9374c281525.jpeg"
      />

      <PageSectionNav
        sections={services.map((s) => ({ id: s.id, label: s.title }))}
        offsetTop={200}
      />

      {/* Services Overview */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-sm text-gray-500 mb-3 uppercase tracking-wider">{t('servicesPage.whatWeOffer')}</p>
            <h2 className="text-4xl md:text-5xl font-serif font-bold text-gray-900 mb-4">
              {t('servicesPage.ministriesPrograms')}
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              {t('servicesPage.ministriesDesc')}
            </p>
          </div>

          {/* Detailed Service Cards */}
          <div className="space-y-16">
            {services.map((service, index) => (
              <div
                key={service.id}
                id={service.id}
                className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-8 items-center scroll-mt-40`}
              >
                {/* Image */}
                <div className="w-full lg:w-1/2">
                  <div className="relative rounded-2xl overflow-hidden shadow-lg">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-56 sm:h-72 md:h-80 object-cover object-top"
                    />
                    <div className="absolute top-4 left-4">
                      <div className="w-14 h-14 flex items-center justify-center bg-white/90 backdrop-blur-sm rounded-xl shadow-md">
                        <i className={`${service.icon} text-2xl text-[#3d1a1e]`}></i>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="w-full lg:w-1/2">
                  <h3 className="text-3xl font-serif font-bold text-gray-900 mb-4">{t(service.titleKey)}</h3>

                  <div className="flex flex-wrap gap-4 mb-5">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <i className="ri-time-line text-amber-600"></i>
                      <span>{t(service.scheduleKey)}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <i className="ri-map-pin-line text-amber-600"></i>
                      <span>{t(service.locationKey)}</span>
                    </div>
                  </div>

                  <p className="text-gray-600 leading-relaxed mb-6">{t(service.descKey)}</p>

                  <div className="grid grid-cols-2 gap-3">
                    {service.featureKeys.map((featureKey, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <i className="ri-check-line text-amber-600"></i>
                        <span className="text-sm text-gray-700">{t(featureKey)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Get Involved CTA */}
      <section className="py-20 bg-gradient-to-br from-[#3d1a1e] to-[#2a1215]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-serif font-bold text-white mb-6">{t('servicesPage.getInvolved')}</h2>
          <p className="text-lg text-white/80 mb-10">
            {t('servicesPage.getInvolvedDesc')}
          </p>

          <form
            id="services-join-us"
            data-local-form
            onSubmit={handleJoinSubmit}
            className="bg-white rounded-2xl p-5 md:p-8 text-left shadow-xl"
          >
            <h3 className="text-xl font-serif font-bold text-gray-900 mb-1">{t('servicesPage.joinFormTitle')}</h3>
            <p className="text-gray-500 mb-4 text-sm">{t('servicesPage.joinFormDesc')}</p>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
              <div>
                <label htmlFor="join-name" className="block text-sm font-medium text-gray-700 mb-1">{t('servicesPage.fullNameLabel')}</label>
                <input
                  id="join-name"
                  name="name"
                  type="text"
                  required
                  placeholder={t('servicesPage.yourName')}
                  className="w-full px-3 py-2.5 rounded-lg border border-gray-200 bg-gray-50 text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-[#3d1a1e]/20 focus:border-[#3d1a1e] transition-all"
                />
              </div>
              <div>
                <label htmlFor="join-email" className="block text-sm font-medium text-gray-700 mb-1">{t('servicesPage.emailLabel')}</label>
                <input
                  id="join-email"
                  name="email"
                  type="email"
                  required
                  placeholder={t('servicesPage.youExample')}
                  className="w-full px-3 py-2.5 rounded-lg border border-gray-200 bg-gray-50 text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-[#3d1a1e]/20 focus:border-[#3d1a1e] transition-all"
                />
              </div>
              <div>
                <label htmlFor="join-phone" className="block text-sm font-medium text-gray-700 mb-1">{t('servicesPage.phoneLabel')} <span className="text-gray-400 font-normal">(opt.)</span></label>
                <input
                  id="join-phone"
                  name="phone"
                  type="tel"
                  placeholder="+1 555 000-0000"
                  className="w-full px-3 py-2.5 rounded-lg border border-gray-200 bg-gray-50 text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-[#3d1a1e]/20 focus:border-[#3d1a1e] transition-all"
                />
              </div>
            </div>

            <div className="mb-3">
              <label htmlFor="join-services" className="block text-sm font-medium text-gray-700 mb-1">{t('servicesPage.serviceInterested')}</label>
              <div className="relative">
                <select
                  id="join-services"
                  name="services"
                  required
                  defaultValue=""
                  className="w-full px-3 py-2.5 pr-10 rounded-lg border border-gray-200 bg-gray-50 text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-[#3d1a1e]/20 focus:border-[#3d1a1e] transition-all appearance-none cursor-pointer"
                >
                  <option value="" disabled>{t('servicesPage.selectService')}</option>
                  {services.map((service) => (
                    <option key={service.id} value={service.title}>
                      {t(service.titleKey)}
                    </option>
                  ))}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-500">
                  <i className="ri-arrow-down-s-line text-lg"></i>
                </div>
              </div>
            </div>

            <div className="mb-4">
              <label htmlFor="join-message" className="block text-sm font-medium text-gray-700 mb-1">{t('servicesPage.messageOpt')} <span className="text-gray-400 font-normal">(optional)</span></label>
              <textarea
                id="join-message"
                name="message"
                rows={2}
                maxLength={500}
                placeholder={t('servicesPage.messageOptDesc')}
                className="w-full px-3 py-2.5 rounded-lg border border-gray-200 bg-gray-50 text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-[#3d1a1e]/20 focus:border-[#3d1a1e] transition-all resize-none"
              />
              <p className="text-xs text-gray-400 mt-1">{t('servicesPage.maxChars')}</p>
            </div>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <button
                type="submit"
                className="px-6 py-2.5 bg-[#3d1a1e] text-white font-semibold rounded-full hover:bg-[#2a1215] transition-all hover:scale-105 cursor-pointer whitespace-nowrap"
              >
                {t('servicesPage.submitRequest')}
              </button>
              <a
                href="/calendar"
                className="px-6 py-2.5 bg-white text-[#3d1a1e] font-semibold rounded-full hover:bg-gray-50 transition-all border border-gray-200 text-center cursor-pointer whitespace-nowrap"
              >
                {t('home.viewFullCalendar')}
              </a>
            </div>
          </form>
        </div>
      </section>

    </PageLayout>
  );
}
